import { Column } from 'react-table'
import { useTranslation } from 'react-i18next'
import { Box, Button, Flex, Text } from 'components'
import Badge, { BadgeColor } from 'components/Badge'
import MenuOptions, { MenuDropdownProps } from 'components/MenuOptions'

import { ApiKey } from 'services/types/api-key'
import { Pagination } from 'types/global'
import { formatDatetime } from 'utils/datetime'
import useModal from 'hooks/use-modal'
import { useMutations } from 'queries/external-api'
import useUserPermissions from 'hooks/use-user-permissions'

import { CellText, ColumnHeader, KeyCell } from './styles'
import RulesModal from './RulesModal'
import ApiKeyForm from './ApiKeyForm'

export const DEFAULT_PAGE = 1
export const DEFAULT_PAGE_SIZE = 10
export const DEFAULT_PAGINATION: Pagination<ApiKey> = {
  pageNumber: DEFAULT_PAGE,
  pageSize: DEFAULT_PAGE_SIZE,
  sortBy: undefined,
}

export const StatusConfig: {
  [key in 'active' | 'expired' | 'deactivated']: {
    badge: { color: BadgeColor; text: string }
    menuOptions: MenuDropdownProps[]
  }
} = {
  active: {
    badge: {
      color: 'green',
      text: 'Active',
    },
    menuOptions: [
      {
        icon: ['fal', 'pencil'],
        label: 'edit_key_lbl',
        value: 'edit',
      },
      {
        icon: ['fal', 'minus-octagon'],
        iconColor: 'red',
        label: 'deactivate_key_lbl',
        value: 'deactivate',
      },
    ],
  },
  expired: {
    badge: {
      color: 'gray',
      text: 'Expired',
    },
    menuOptions: [
      {
        icon: ['fal', 'pencil'],
        label: 'edit_key_lbl',
        value: 'edit',
      },
    ],
  },
  deactivated: {
    badge: {
      color: 'red',
      text: 'Revoked',
    },
    menuOptions: [
      {
        icon: ['far', 'repeat'],
        label: 'reactivate_key_lbl',
        value: 'reactivate',
      },
    ],
  },
}

const statusKey = (status: string) => {
  switch (status) {
    case 'Active':
      return 'active'
    case 'Expired':
      return 'expired'
    case 'Revoked':
      return 'deactivated'
    default:
      return 'active'
  }
}

const useApiKeysHooks = () => {
  const { invalidateLists, activate, deActivate } = useMutations()
  const { setModal, setConfirmationModal } = useModal()
  const [{ canEdit }] = useUserPermissions('externalApi')
  const { t } = useTranslation()

  const handleRowClick = (row: ApiKey) => {
    setModal(
      <ApiKeyForm
        data={row}
        onClose={(success) => {
          setModal(null)
          success && invalidateLists()
        }}
      />,
    )
  }

  const columns: Column<ApiKey>[] = [
    {
      accessor: 'key',
      Cell: ({ row }) => <KeyCell keyValue={row.original.key} />,
      disableSortBy: true,
      Header: () => <ColumnHeader>API Keys</ColumnHeader>,
      id: 'key',
    },
    {
      accessor: 'rules',
      Cell: ({ row }) => {
        if (!row.original.rules?.length) return '-'

        const display = row.original.rules
          ?.map((i) => {
            const ownerName =
              i.lead_owner && i.lead_owner.user_id ? `${i.lead_owner.fname} ${i.lead_owner.lname}` : null
            const teamName = i.lead_owner && i.lead_owner.team_id ? `${i.lead_owner.team_name}` : null
            const owner = teamName ? teamName : ownerName ?? t('unassigned_lbl')
            const campaignName = i.campaign && i.campaign.campaign_name ? i.campaign.campaign_name : 'None'
            return campaignName || owner ? `${campaignName} - (${owner})` : '-'
          })
          .join(', ')
        return (
          <Flex flexWrap="wrap" gap="0.5rem" position="relative">
            <Text ellipsis height="2rem" maxWidth="calc(100% - 100px)">
              {display}
            </Text>
            <Box position="absolute" right="0" top="0">
              <Button
                variant="outline"
                size="small"
                onClick={() => setModal(<RulesModal data={row.original.rules} onClose={() => setModal(null)} />)}
              >
                View rules
              </Button>
            </Box>
          </Flex>
        )
      },
      disableSortBy: true,
      Header: () => <ColumnHeader>Rules</ColumnHeader>,
      id: 'rules',
      width: 300,
    },
    {
      accessor: 'remarks',
      Cell: ({ row }) => <CellText textTransform="none">{row.original.remarks ?? '-'}</CellText>,
      disableSortBy: true,
      Header: () => <ColumnHeader>Remarks</ColumnHeader>,
      id: 'remarks',
      width: 150,
    },
    {
      accessor: 'expiration_ts',
      Cell: ({ row }) => <CellText>{formatDatetime(row.original.expiration_ts, 'dd MMM, yyyy') ?? '-'}</CellText>,
      Header: () => <ColumnHeader>Expiry date</ColumnHeader>,
      id: 'expiration_ts',
      width: 150,
    },
    {
      accessor: 'status',
      Cell: ({ row }) => <Badge {...StatusConfig[statusKey(row.original.status)].badge} />,
      Header: () => <ColumnHeader>status_lbl</ColumnHeader>,
      id: 'status',
      width: 80,
    },
  ]

  if (canEdit) {
    columns.push({
      accessor: 'api_key_id',
      Cell: ({ row }) => (
        <MenuOptions
          absoluteParent={true}
          handleClickOnItem={(value) => {
            switch (value) {
              case 'edit':
                handleRowClick(row.original)
                break
              case 'deactivate':
                setConfirmationModal({
                  submit: async () => {
                    await deActivate.mutateAsync({ api_key_id: row.original.api_key_id, is_active: false })
                  },
                  variant: 'deactivate',
                })
                break
              case 'reactivate':
                setConfirmationModal({
                  submit: async () => {
                    await activate.mutateAsync({ api_key_id: row.original.api_key_id, is_active: true })
                  },
                  variant: row.original.is_active ? 'deactivate' : 'reactivate',
                })
                break
              default:
            }
          }}
          options={StatusConfig[statusKey(row.original.status)].menuOptions}
        />
      ),
      disableSortBy: true,
      Header: () => <div />,
      id: 'api_key_id',
      width: 50,
    })
  }

  return {
    columns,
  }
}

export default useApiKeysHooks
